/* Bildirim Merkezi zili + açılır panel (topbar) */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell, Radio, Briefcase, CreditCard, UserRound, AlertTriangle, CheckCheck,
} from "lucide-react";
import { http } from "@/lib/api";

const notifApi = {
  list: () => http.get("/notifications?limit=40").then((r) => r.data),
  readOne: (id) => http.post(`/notifications/${id}/read`).then((r) => r.data),
  readAll: () => http.post("/notifications/read-all").then((r) => r.data),
};

const TYPE_META = {
  signal: { Icon: Radio, color: "var(--acc)", label: "Sinyal" },
  position: { Icon: Briefcase, color: "var(--gold)", label: "Pozisyon" },
  payment: { Icon: CreditCard, color: "#0A84FF", label: "Ödeme" },
  user: { Icon: UserRound, color: "var(--up)", label: "Kullanıcı" },
  system: { Icon: AlertTriangle, color: "var(--down)", label: "Sistem" },
};

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "az önce";
  if (s < 3600) return `${Math.floor(s / 60)} dk önce`;
  if (s < 86400) return `${Math.floor(s / 3600)} sa önce`;
  return `${Math.floor(s / 86400)} gün önce`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(null); // null = tümü
  const ref = useRef(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: notifApi.list,
    refetchInterval: 30000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const readOne = useMutation({ mutationFn: notifApi.readOne, onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: notifApi.readAll, onSuccess: invalidate });

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const unread = data?.unread_count || 0;
  const items = (data?.items || []).filter((n) => !filter || n.type === filter);

  const click = (n) => {
    if (!n.read) readOne.mutate(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="tx-ib"
        title="Bildirimler"
        onClick={() => setOpen(!open)}
        data-testid="topbar-notif-btn"
      >
        {unread > 0 && (
          <span
            data-testid="notif-unread-badge"
            style={{
              position: "absolute", top: 4, right: 4, minWidth: 15, height: 15,
              padding: "0 3px", borderRadius: 999, background: "var(--acc)",
              color: "#000", fontSize: 9, fontWeight: 800,
              fontFamily: "var(--fm)", display: "grid", placeItems: "center",
              lineHeight: 1, zIndex: 1,
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        <Bell size={16} strokeWidth={1.8} />
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 10px)",
            width: 380, maxWidth: "90vw", maxHeight: 480, zIndex: 80,
            border: "1px solid var(--line)", borderRadius: 12,
            background: "rgba(12,12,12,0.97)", backdropFilter: "blur(16px)",
            boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}
        >
          <div style={{
            padding: "10px 14px", borderBottom: "1px solid var(--line)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <b style={{ fontSize: 13 }}>
              Bildirimler{unread > 0 && (
                <span style={{ color: "var(--acc)", fontFamily: "var(--fm)", fontSize: 11 }}>
                  {" "}· {unread} okunmamış
                </span>
              )}
            </b>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => readAll.mutate()}
                data-testid="notif-read-all-btn"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  fontSize: 10, fontFamily: "var(--fm)", color: "var(--muted)",
                  background: "none", border: "1px solid var(--line)",
                  borderRadius: 6, padding: "3px 8px", cursor: "pointer",
                }}
              >
                <CheckCheck size={11} /> Tümünü okundu yap
              </button>
            )}
          </div>

          <div style={{
            padding: "8px 12px", display: "flex", gap: 6, flexWrap: "wrap",
            borderBottom: "1px solid var(--line)",
          }}>
            {[[null, "Tümü"], ...Object.entries(TYPE_META).map(([k, v]) => [k, v.label])].map(
              ([key, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setFilter(key)}
                  style={{
                    fontSize: 9, fontFamily: "var(--fm)", letterSpacing: "0.08em",
                    textTransform: "uppercase", padding: "3px 9px", borderRadius: 999,
                    cursor: "pointer",
                    border: `1px solid ${filter === key ? "var(--acc-line)" : "var(--line)"}`,
                    background: filter === key ? "var(--acc-dim)" : "transparent",
                    color: filter === key ? "var(--acc)" : "var(--faint)",
                  }}
                >
                  {label}
                </button>
              )
            )}
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {items.length === 0 ? (
              <div style={{
                padding: 28, textAlign: "center", color: "var(--faint)",
                fontFamily: "var(--fm)", fontSize: 11,
              }}>
                Bildirim yok.
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || TYPE_META.system;
                const { Icon } = meta;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => click(n)}
                    data-testid={`notif-item-${n.id}`}
                    style={{
                      display: "flex", gap: 10, width: "100%", textAlign: "left",
                      padding: "10px 14px", cursor: "pointer", border: "none",
                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                      background: n.read ? "transparent" : "rgba(255,93,21,0.05)",
                    }}
                  >
                    <Icon size={15} style={{ color: meta.color, flexShrink: 0, marginTop: 2 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: 12, fontWeight: n.read ? 500 : 700,
                        color: n.read ? "var(--muted)" : "var(--txt)",
                      }}>
                        {n.title}
                      </div>
                      {n.body && (
                        <div style={{
                          fontSize: 11, color: "var(--faint)", marginTop: 2,
                          lineHeight: 1.4,
                        }}>
                          {n.body}
                        </div>
                      )}
                      <div style={{
                        fontSize: 9, fontFamily: "var(--fm)", color: "var(--faint)",
                        marginTop: 3, textTransform: "uppercase", letterSpacing: "0.08em",
                      }}>
                        {meta.label} · {timeAgo(n.created_at)}
                        {n.urgent && <span style={{ color: "var(--down)" }}> · ACİL</span>}
                      </div>
                    </div>
                    {!n.read && (
                      <span style={{
                        width: 7, height: 7, borderRadius: 999, background: "var(--acc)",
                        flexShrink: 0, marginLeft: "auto", marginTop: 6,
                      }} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
